/**
 * Custom Hook: useFacilitiesInfinite
 * Handles infinite scrolling pagination for facilities using React Query
 *
 * Features:
 * - Cursor-based pagination
 * - Manual "Load More" button
 * - Proper cache management
 * - Optimistic updates (handled where you mutate)
 * - Error handling
 */

import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getFacilitiesV3Paginated } from '../services/facilitiesService';

/** Query key prefix for facilities infinite list (Step 1). Use for refetch after create/update/delete/duplicate. */
export const FACILITIES_QUERY_KEY = ['facilities'];

/**
 * @typedef {Object} UseFacilitiesInfiniteParams
 * @property {string} organizationId
 * @property {string} [queryScope] - Isolates cache per context (e.g. 'organization-structure' vs 'cleaning-standards') so pagination in one step does not affect the other
 * @property {number} [limit]
 * @property {string} [search]
 * @property {'name'|'createdAt'|'updatedAt'} [sortBy]
 * @property {'asc'|'desc'} [sortOrder]
 */

export const useFacilitiesInfinite = (params = {}) => {
  const {
    organizationId,
    queryScope = 'default',
    enabled = true,
    limit = 10,
    search = '',
    sortBy = 'createdAt',
    sortOrder = 'desc',
  } = params;

  const searchTerm = typeof search === 'string' ? search.trim() : '';
  const scope = typeof queryScope === 'string' && queryScope ? queryScope : 'default';

  const query = useInfiniteQuery({
    queryKey: [...FACILITIES_QUERY_KEY, scope, organizationId, limit, searchTerm, sortBy, sortOrder],
    enabled: !!enabled && !!organizationId,
    queryFn: ({ pageParam, signal }) => {
      return getFacilitiesV3Paginated({
        limit,
        search: searchTerm,
        sortBy,
        sortOrder,
        cursor: pageParam,
        signal,
      });
    },
    getNextPageParam: (lastPage) => {
      const { nextCursor } = lastPage.pagination ?? {};
      return nextCursor ?? undefined;
    },
    initialPageParam: undefined,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Memoize flattened array so consumers get stable reference when data unchanged (avoids re-render loops)
  const facilities = useMemo(
    () => query.data?.pages?.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  const totalCount = query.data?.pages?.[query.data.pages.length - 1]?.pagination?.totalCount ?? 0;

  const hasNextPage = query.hasNextPage ?? false;

  return {
    facilities,
    totalCount,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch,
  };
};
